import { TableContent } from "@prismicio/types-internal/lib/content"
import {
  type RichText,
  type Table,
  TableCell as TableCellConfig,
} from "@prismicio/types-internal/lib/customtypes"

import { RichTextField } from "../RichTextField"
import {
  type ExtendedRichTextContent,
  ExtendedRichTextNodeType,
  type TableBlock,
} from "../RichTextField/types"

interface Props {
  id: string
  field: Table
  readOnly: boolean
  content: TableContent | undefined
  onContentChange: (content: TableContent | undefined) => void
}

const defaultTableContent = buildEmptyTableContent(2, 3)
const minimalTableContent: TableContent = {
  __TYPE__: "TableContent",
  content: buildEmptyTableContent(1, 1),
}

export function TableField(props: Props) {
  const { field, content: tableContent, onContentChange, ...fieldProps } = props

  function handleContentChange(content: ExtendedRichTextContent | undefined) {
    if (!content) return onContentChange(minimalTableContent)
    props.onContentChange(mapRichTextContentToTableContent(content))
  }

  return (
    <RichTextField
      {...fieldProps}
      field={mapTableToRichText(field)}
      content={mapTableContentToRichTextContent(tableContent)}
      onContentChange={handleContentChange}
      scroll="x-auto"
      padding="zero"
    />
  )
}

function buildEmptyTableContent(
  rows: number,
  columns: number,
): TableBlock["content"] {
  return Array.from({ length: rows }, () => ({
    type: "tableRow",
    content: Array.from({ length: columns }, () => ({
      type: "tableCell",
      content: {
        __TYPE__: "StructuredTextContent",
        value: [{ type: "paragraph", content: { text: "" } }],
      },
    })),
  }))
}

function mapTableContentToRichTextContent(
  tableContent: TableContent | undefined,
): ExtendedRichTextContent {
  return {
    __TYPE__: "StructuredTextContent",
    value: [{ type: "table", content: prepareTableContent(tableContent) }],
  }
}

function prepareTableContent(tableContent: TableContent | undefined) {
  // Decoded the content using it's codec to make sure it's valid.
  const decodedTable = TableContent.decode(tableContent)
  if (decodedTable._tag === "Left") return defaultTableContent

  // Check whether the table has content, if not, use a default one.
  const tableHasContent = decodedTable.right.content.some((row) =>
    row.content.some((cell) => cell.content.value.some(({ type }) => type)),
  )
  if (!tableHasContent) return defaultTableContent

  return decodedTable.right.content
}

function mapRichTextContentToTableContent(
  content: ExtendedRichTextContent,
): TableContent {
  return {
    __TYPE__: "TableContent",
    content: content.value.flatMap((block) =>
      block.type === "table" ? block.content : [],
    ),
  }
}

function mapTableToRichText(field: Table): RichText {
  return {
    ...TableCellConfig,
    config: {
      ...TableCellConfig.config,
      label: field.config?.label,
      allowTargetBlank: true,
      multi: [
        TableCellConfig.config.multi,
        ExtendedRichTextNodeType.table,
      ].join(","),
    },
  }
}
